<template>
    <q-table
        ref="mainTable"
        :rows="processedRows"
        :columns="table.columns"
        v-model:pagination="tablePagination"
        :no-data-label="$t('No records found for this date range.')"
        :hide-bottom="report.rows.length > 0"
        :table-class="
            report.rows.length > 0
                ? 'report-container sticky-table sticky-column'
                : ''
        "
        row-key="asset_id"
        class="sticky-column full-width bg-secondary-white no-shadow border-grey"
        :title="label"
        title-class="text-weight-medium text-medium-grey text-body2"
        :sort-method="customSort"
        binary-state-sort
    >
        <!-- Header slot -->
        <template v-slot:header="props">
            <q-tr :props="props">
                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                    <span class="text-weight-bold" style="font-size: 14px">{{
                        col.label
                    }}</span>
                    <q-tooltip class="text-body2" v-if="col.tipText">
                        {{ col.tipText }}
                    </q-tooltip>
                </q-th>
            </q-tr>
        </template>

        <template v-slot:body="props">
            <q-tr :props="props">
                <q-td key="asset_name">
                    <div class="row">
                        <div>
                            <q-btn
                                @click="props.expand = !props.expand"
                                :icon="
                                    props.expand
                                        ? 'fas fa-caret-up'
                                        : 'fas fa-caret-down'
                                "
                                :color="
                                    hasExpandableContent(props.row)
                                        ? 'accent'
                                        : 'grey-6'
                                "
                                size="sm"
                                round
                                dense
                                class="q-mr-md"
                                :disable="!hasExpandableContent(props.row)"
                            >
                                <q-tooltip
                                    v-if="hasExpandableContent(props.row)"
                                >
                                    {{
                                        props.expand
                                            ? $t('Collapse row')
                                            : $t('Expand row')
                                    }}
                                </q-tooltip>
                                <q-tooltip v-else>
                                    {{ $t('No equipment failures') }}
                                </q-tooltip>
                            </q-btn>

                            {{ props.row.asset_name }}

                            <q-badge
                                v-if="isAssetDeleted(props.row.asset_id)"
                                color="lighter-grey"
                                text-color="medium-grey"
                                class="q-ml-xs"
                            >
                                {{ $t('Deleted') }}
                            </q-badge>
                        </div>
                    </div>
                </q-td>
                <q-td key="running_duration_min" align="right">
                    {{ formatDuration(props.row.running_duration_min) }}
                </q-td>
                <q-td key="downtime_duration_min" align="right">
                    {{ formatDuration(props.row.downtime_duration_min) }}
                </q-td>
                <q-td key="equipment_failure_duration_min" align="right">
                    {{
                        formatDuration(props.row.equipment_failure_duration_min)
                    }}
                </q-td>
                <q-td key="number_downtimes" align="right">
                    {{ props.row.number_downtimes }}
                </q-td>
                <q-td key="number_equipment_failures" align="right">
                    {{ props.row.number_equipment_failures }}
                </q-td>
                <q-td key="mttr_min" align="right" class="mttr-bg">
                    {{ formatDuration(props.row.mttr_min) }}
                </q-td>
                <q-td key="mtbf_min" align="right" class="mtbf-bg">
                    {{ formatDuration(props.row.mtbf_min) }}
                </q-td>
            </q-tr>
            <template v-if="props.expand">
                <template
                    v-for="(cause, index) in sortedCausesFor(props.row)"
                    :key="`${props.row.asset_id}-${cause.cause_id}-${index}`"
                >
                    <q-tr class="expanded-child-row">
                        <q-td align="left">
                            <div class="expanded-child">
                                {{ cause.cause_name }}
                            </div>
                        </q-td>
                        <q-td align="right">
                            {{ formatDuration(cause.running_duration_min) }}
                        </q-td>
                        <q-td align="right">
                            {{ formatDuration(cause.downtime_duration_min) }}
                        </q-td>
                        <q-td align="right">
                            {{
                                formatDuration(
                                    cause.equipment_failure_duration_min
                                )
                            }}
                        </q-td>
                        <q-td align="right">
                            {{ cause.number_downtimes }}
                        </q-td>
                        <q-td align="right">
                            {{ cause.number_equipment_failures }}
                        </q-td>
                        <q-td align="right" class="mttr-bg">
                            {{ formatDuration(cause.mttr_min) }}
                        </q-td>
                        <q-td align="right" class="mtbf-bg">
                            {{ formatDuration(cause.mtbf_min) }}
                        </q-td>
                    </q-tr>
                </template>
            </template>
        </template>

        <template v-slot:bottom-row>
            <q-tr
                align="right"
                v-show="report.rows.length > 0"
                class="text-weight-medium bg-secondary-white"
            >
                <q-td align="left" class="bg-secondary-white">{{
                    $t('Summary')
                }}</q-td>
                <q-td>
                    {{ formatDuration(report.totals.running_duration_min, 2) }}
                </q-td>
                <q-td>
                    {{ formatDuration(report.totals.downtime_duration_min, 2) }}
                </q-td>
                <q-td>
                    {{
                        formatDuration(
                            report.totals.equipment_failure_duration_min,
                            2
                        )
                    }}
                </q-td>
                <q-td>
                    {{ report.totals.number_downtimes }}
                </q-td>
                <q-td>
                    {{ report.totals.number_equipment_failures }}
                </q-td>
                <q-td>
                    {{ formatDuration(report.totals.mttr_min, 2) }}
                </q-td>
                <q-td>
                    {{ formatDuration(report.totals.mtbf_min, 2) }}
                </q-td>
            </q-tr>
        </template>
    </q-table>
</template>

<script>
import { getLocaleFormat } from '@models/timeformats.js';
import { mapState } from 'pinia';
import { useAssetsStore } from '@stores/assets.ts';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'MaintenanceReportTable',
    props: {
        /**
         * Report data.
         */
        report: {
            type: Object,
            required: true
        },
        label: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            // For tracking sort and pagination state
            tablePagination: {
                sortBy: 'number_equipment_failures',
                descending: true,
                page: 1,
                rowsPerPage: 0 // 0 means show all rows
            },
            table: {
                columns: [
                    {
                        name: 'asset_name',
                        field: 'asset_name',
                        label: this.$t('Asset'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'running_duration_min',
                        field: 'running_duration_min',
                        label: this.$t('Running (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The accumulated running time during the selected date range.'
                        ),
                        format: val => this.formatDuration(val, 2),
                        sortable: true
                    },
                    {
                        name: 'downtime_duration_min',
                        field: 'downtime_duration_min',
                        label: this.$t('Total downtime (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The accumulated downtime during the selected date range.'
                        ),
                        format: val => this.formatDuration(val, 2),
                        sortable: true
                    },
                    {
                        name: 'equipment_failure_duration_min',
                        field: 'equipment_failure_duration_min',
                        label: this.$t('Equipment failure duration (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The accumulated duration of downtime related to equipment failure during the selected date range.'
                        ),
                        format: val => this.formatDuration(val, 2),
                        sortable: true
                    },
                    {
                        name: 'number_downtimes',
                        field: 'number_downtimes',
                        label: this.$t('Number of downtimes'),
                        align: 'right',
                        tipText: this.$t(
                            'The total number of downtimes during the selected date range.'
                        ),
                        sortable: true
                    },
                    {
                        name: 'number_equipment_failures',
                        field: 'number_equipment_failures',
                        label: this.$t('Number of equipment failures'),
                        align: 'right',
                        tipText: this.$t(
                            'The total number of Equipment failure-related downtimes during the selected date range.'
                        ),
                        sortable: true
                    },
                    {
                        name: 'mttr_min',
                        field: 'mttr_min',
                        label: this.$t('MTTR (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'MTTR (Mean Time To Repair) is the average time it takes to have an asset running after an Equipment failure-related downtime has occurred for the selected date range.'
                        ),
                        classes: 'mttr-bg',
                        format: val => this.formatDuration(val, 2),
                        sortable: true
                    },
                    {
                        name: 'mtbf_min',
                        field: 'mtbf_min',
                        label: this.$t('MTBF (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'MTBF (Mean Time Between Failures) is the average time between Equipment failure-related downtimes for the selected date range.'
                        ),
                        classes: 'mtbf-bg',
                        format: val => this.formatDuration(val, 2),
                        sortable: true
                    }
                ],
                pagination: {
                    rowsPerPage: 0 // infinite
                }
            }
        };
    },
    computed: {
        ...mapState(useAssetsStore, ['getAsset']),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),

        /**
         * Returns a function that sorts causes for a row using the current sort parameters.
         * @returns {Function} A function that takes a row and returns its sorted causes.
         */
        sortedCausesFor() {
            // Return a function that will be called for each row
            return row => {
                if (!row || !row.filteredCauses || !row.filteredCauses.length) {
                    return [];
                }

                // Access the table's current sort parameters from pagination
                const sortBy = this.tablePagination.sortBy;
                const sortDesc = this.tablePagination.descending;

                // Make a copy and sort
                return [...row.filteredCauses].sort((a, b) => {
                    return this.sortItems(a, b, sortBy, sortDesc);
                });
            };
        },

        // Process rows to filter causes to only show equipment failures
        processedRows() {
            if (!this.report || !this.report.rows || !this.report.rows.length) {
                return [];
            }

            return this.report.rows.map(row => {
                // Create a shallow copy of the row
                const newRow = { ...row };

                // Filter causes to include equipment failures and the non-equipment failures group
                newRow.filteredCauses = row.causes
                    ? row.causes.filter(
                          cause =>
                              cause.equipment_failure === true ||
                              cause.cause_id === 'non_equipment_failures'
                      )
                    : [];

                // Return the modified row
                return newRow;
            });
        }
    },
    methods: {
        /**
         * Format the duration with locale-specific number formatting
         * @param {Number} val - The numeric value to format
         * @param {Number} digits - The number of decimal places (default: 2)
         * @returns {String} Formatted duration with appropriate thousand separators and decimal places
         */
        formatDuration(val, digits = 2) {
            if (val === null || val === undefined) {
                return '';
            }
            return Number(val).toLocaleString(this.userLocale, {
                minimumFractionDigits: digits,
                maximumFractionDigits: digits
            });
        },
        /**
         * Check if an asset is deleted.
         * @param {string} asset_id - The ID of the asset to check.
         * @returns {boolean} - True if the asset is deleted, false otherwise.
         */
        isAssetDeleted(asset_id) {
            const asset = this.getAsset(asset_id);
            if (asset) {
                return asset.is_deleted;
            }
            return false;
        },
        /**
         * Checks if a row has expandable content (filtered causes).
         * @param {Object} row - The row to check.
         * @returns {boolean} - True if the row has equipment failures, false if it only has non-equipment failures or no causes.
         */
        hasExpandableContent(row) {
            // Only allow expansion if there are filtered causes AND at least one is an equipment failure
            if (!row.filteredCauses || row.filteredCauses.length === 0) {
                return false;
            }

            // Check if there's at least one equipment failure
            // If there's only the 'non_equipment_failures' group and no equipment failures,
            // don't allow expansion
            return row.filteredCauses.some(
                cause => cause.equipment_failure === true
            );
        },

        /**
         * Helper method to sort items consistently across the table.
         * @param {Object} a - First item to compare.
         * @param {Object} b - Second item to compare.
         * @param {string} sortField - Field to sort by.
         * @param {boolean} sortDesc - Whether to sort in descending order.
         * @returns {number} - Comparison result for sorting.
         */
        sortItems(a, b, sortField, sortDesc) {
            if (!sortField) return 0;

            const valueA = a[sortField];
            const valueB = b[sortField];

            // Special case for string values
            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return sortDesc
                    ? valueB.localeCompare(valueA)
                    : valueA.localeCompare(valueB);
            }

            // Default numerical comparison (handles duration values as well)
            return sortDesc ? valueB - valueA : valueA - valueB;
        },

        /**
         * Custom sort method for the table data.
         * @param {Array} rows - The rows to sort.
         * @param {string} sortBy - The field to sort by.
         * @param {boolean} descending - Whether to sort in descending order.
         * @returns {Array} - The sorted array of rows.
         */
        customSort(rows, sortBy, descending) {
            // Update our pagination object to keep track of current sort
            this.tablePagination.sortBy = sortBy;
            this.tablePagination.descending = descending;

            const data = [...rows];

            if (sortBy) {
                data.sort((a, b) => this.sortItems(a, b, sortBy, descending));
            }

            return data;
        }
    }
};
</script>

<style lang="scss" scoped>
$row-color: $secondary-white;

// table header
:deep(.sticky-table thead tr:nth-child(1) th) {
    background-color: $row-color;
}

// reports panel
:deep(.report-container) {
    overflow-y: scroll;
}

:deep(.mttr-bg) {
    background-color: $decorative-blue;
}

:deep(.mtbf-bg) {
    background-color: $decorative-blue;
}

// sticky columns
:deep(.sticky-column) {
    background-color: white;
}

:deep(.sticky-column tr th) {
    background: white;
    position: sticky;
    z-index: 2;
}

:deep(.sticky-column thead tr:last-child th) {
    top: 48px;
    z-index: 3;
}

:deep(.sticky-column td:nth-child(1)) {
    background-color: white;
}

:deep(.sticky-column thead tr:nth-child(1) th) {
    top: 0;
    z-index: 1;
}

:deep(.sticky-column tr:nth-child(1) th:nth-child(1)) {
    z-index: 3;
}

:deep(.sticky-column td:nth-child(1)) {
    z-index: 1;
}

:deep(.sticky-column td:nth-child(1)),
:deep(.sticky-column th:nth-child(1)) {
    left: 0;
    position: sticky;
}

:deep(.q-tr.expanded-child-row) {
    background-color: rgba(0, 0, 0, 0.03);
}

:deep(.expanded-child) {
    margin-left: 60px !important;
}
</style>
